import { useEffect, useState } from "react";
import { useArtis } from "./useArtis.js";
const byOrder = (a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0);
/**
 * The user's pages: subscribes to `artis.v1.pages` with the same optional `filters`, folds
 * the delta stream into a snapshot and returns it sorted by `order`. Starts empty; catches up
 * immediately once the SDK is live. Returns `[]` while the SDK is down. A changed filter
 * resubscribes with a fresh snapshot.
 */
export const usePages = (filters) => {
    const artis = useArtis();
    const type = filters?.type;
    const [pages, setPages] = useState([]);
    useEffect(() => {
        if (!artis) {
            setPages([]);
            return;
        }
        const byId = new Map();
        setPages([]);
        return artis.v1.pages.subscribe((delta) => {
            for (const p of delta.upserts ?? [])
                byId.set(p.id, p);
            for (const id of delta.removes ?? [])
                byId.delete(id);
            setPages([...byId.values()].sort(byOrder));
        }, type === undefined ? undefined : { type });
    }, [artis, type]);
    return pages;
};
/**
 * Whether the worker's full page list has arrived. `usePages()` is `[]` both before that
 * and when the user has no pages; this distinguishes the two.
 */
export const usePagesReady = () => {
    const artis = useArtis();
    const [ready, setReady] = useState(false);
    useEffect(() => {
        if (!artis) {
            setReady(false);
            return;
        }
        setReady(false);
        return artis.v1.pages.subscribe((delta) => {
            if (delta.snapshot)
                setReady(true);
        });
    }, [artis]);
    return ready;
};
/** One page by id from the same snapshot {@link usePages} keeps, or `undefined` while unknown. */
export const usePage = (id) => usePages().find((p) => p.id === id);
/**
 * One page's columns: subscribes to `artis.v1.pages.columns` for `pageId`, folds the deltas
 * and returns them sorted by `order`. Returns `[]` while the SDK is down, and resets when
 * `pageId` changes.
 */
export const usePageColumns = (pageId) => {
    const artis = useArtis();
    const [columns, setColumns] = useState([]);
    useEffect(() => {
        if (!artis) {
            setColumns([]);
            return;
        }
        const byId = new Map();
        setColumns([]);
        return artis.v1.pages.columns.subscribe(pageId, (delta) => {
            for (const c of delta.upserts ?? [])
                byId.set(c.id, c);
            for (const id of delta.removes ?? [])
                byId.delete(id);
            setColumns([...byId.values()].sort(byOrder));
        });
    }, [artis, pageId]);
    return columns;
};
/**
 * Whether the worker's full column list has arrived for this session. Until then
 * {@link usePageColumns} is `[]` for a page that may still have columns.
 */
export const usePageColumnsReady = (pageId) => {
    const artis = useArtis();
    const [ready, setReady] = useState(false);
    useEffect(() => {
        if (!artis) {
            setReady(false);
            return;
        }
        setReady(false);
        return artis.v1.pages.columns.subscribe(pageId, (delta) => {
            if (delta.snapshot)
                setReady(true);
        });
    }, [artis, pageId]);
    return ready;
};
//# sourceMappingURL=usePages.js.map